import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { OrgRole, Role } from '@soc/shared';
import {
  useCreateSuperAdminMutation,
  useCreateUserMutation,
  useDeleteUserMutation,
  useGetOrganizationsQuery,
  useGetProjectsQuery,
  useGetUsersQuery,
  useUpdateSuperAdminMutation,
  useUpdateUserMutation,
} from '../app/api';
import { useAuth } from '../features/auth/useAuth';
import { apiErrorMessage, isValidEmail } from '../lib/validation';
import { ErrorNote, Field, initials, inputStyle, primaryButtonStyle } from '../features/admin/adminUi';
import {
  AdminFormShell,
  FormColumn,
  FormHeader,
  FormSection,
  RecordMeta,
  SegmentedControl,
  dangerBtnStyle,
  secondaryBtnStyle,
} from '../features/admin/formUi';

const ROLE_OPTIONS = [
  { value: 'user', label: 'Viewer', desc: 'Read-only access to maps & alerts' },
  { value: 'admin', label: 'Admin', desc: 'Manage users, projects & detection in the org' },
];
// Only super_admins may assign this; the card is offered to super_admin callers.
const SUPER_ADMIN_OPTION = {
  value: 'super_admin',
  label: 'Super admin',
  desc: 'Internal — full cross-org access, no organization',
};

// Cosmetic, role-derived permission preview (read-only — follows the role).
const PERMS: { name: string; roles: OrgRole[] }[] = [
  { name: 'View maps & alerts', roles: ['user', 'admin'] },
  { name: 'Acknowledge alerts', roles: ['admin'] },
  { name: 'Edit detection rules', roles: ['admin'] },
  { name: 'Edit monitored areas', roles: ['admin'] },
  { name: 'Manage users', roles: ['admin'] },
];

export default function UserForm({ mode }: { mode: 'create' | 'edit' }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const { role: myRole } = useAuth();
  const isSuper = myRole === 'super_admin';

  const { data: users } = useGetUsersQuery();
  const { data: orgs } = useGetOrganizationsQuery(undefined, { skip: !isSuper });
  const { data: projects } = useGetProjectsQuery();

  const [createUser, { isLoading: creating }] = useCreateUserMutation();
  const [updateUser, { isLoading: updating }] = useUpdateUserMutation();
  const [createSuperAdmin, { isLoading: creatingSuper }] = useCreateSuperAdminMutation();
  const [updateSuperAdmin, { isLoading: updatingSuper }] = useUpdateSuperAdminMutation();
  const [deleteUser] = useDeleteUserMutation();

  const record = mode === 'edit' ? users?.items.find((u) => u.id === id) : undefined;
  const isSuperAdminRecord = record?.role === 'super_admin';

  // real, persisted fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [roleSel, setRoleSel] = useState<Role>('user');
  const [orgId, setOrgId] = useState('');
  // cosmetic, non-persisted (base infra — projects picklist)
  const [assigned, setAssigned] = useState<string[]>([]);

  const [error, setError] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(mode === 'create');

  useEffect(() => {
    if (mode === 'edit' && record && !hydrated) {
      setName(record.name);
      setEmail(record.email);
      setOrgId(record.orgId ?? '');
      if (record.role !== 'super_admin') setRoleSel(record.role);
      setHydrated(true);
    }
  }, [mode, record, hydrated]);

  // Only super_admins may create a super_admin, and only in create mode (an
  // existing super_admin is edited in place; org users aren't "promoted" here).
  const canCreateSuper = mode === 'create' && isSuper;
  // The role selector is locked when editing an existing super_admin (no demotion).
  const roleLocked = isSuperAdminRecord;
  const activeRole: Role = isSuperAdminRecord ? 'super_admin' : roleSel;
  const isSuperRole = activeRole === 'super_admin';

  const emailValid = isValidEmail(email);
  const nameValid = name.trim().length > 0;
  // super_admins are org-less, so an org is only required for org-scoped users.
  const orgRequired = isSuper && mode === 'create' && !isSuperRole;
  const orgValid = !orgRequired || orgId.length > 0;
  const saving = creating || updating || creatingSuper || updatingSuper;
  const canSave = nameValid && emailValid && orgValid && !saving;

  // Projects available to assign (cosmetic): scoped to the chosen/owning org.
  const orgProjects = (projects?.items ?? []).filter((p) =>
    isSuper ? p.orgId === (orgId || record?.orgId || null) : true,
  );

  const submit = async () => {
    setError(null);
    if (!canSave) return;
    try {
      if (mode === 'create') {
        if (roleSel === 'super_admin') {
          // dedicated, super_admin-guarded create (org-less)
          await createSuperAdmin({ email: email.trim(), name: name.trim() }).unwrap();
        } else {
          await createUser({
            email: email.trim(),
            name: name.trim(),
            role: roleSel,
            ...(isSuper ? { orgId } : {}),
          }).unwrap();
        }
      } else if (id) {
        if (isSuperAdminRecord) {
          await updateSuperAdmin({ id, name: name.trim() }).unwrap();
        } else {
          await updateUser({
            id,
            name: name.trim(),
            ...(roleSel !== 'super_admin' ? { role: roleSel } : {}),
          }).unwrap();
        }
      }
      navigate('/users');
    } catch (e) {
      setError(apiErrorMessage(e, 'Failed to save user'));
    }
  };

  const remove = async () => {
    if (!id) return;
    if (!window.confirm('Remove this user?')) return;
    try {
      await deleteUser(id).unwrap();
      navigate('/users');
    } catch (e) {
      setError(apiErrorMessage(e, 'Failed to remove user'));
    }
  };

  const toggleAssigned = (v: string) =>
    setAssigned((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));

  const actions = (
    <>
      {mode === 'edit' && !isSuperAdminRecord && (
        <button type="button" style={dangerBtnStyle} onClick={remove}>
          Remove user
        </button>
      )}
      <Link to="/users" style={secondaryBtnStyle}>
        Cancel
      </Link>
      <button type="button" style={primaryButtonStyle(canSave)} disabled={!canSave} onClick={submit}>
        {saving ? 'Saving…' : mode === 'create' ? 'Create user' : 'Save changes'}
      </button>
    </>
  );

  // Offer the Super admin card when a super_admin is creating a user, or when
  // editing an existing super_admin (read-only display).
  const roleOptions =
    canCreateSuper || isSuperAdminRecord ? [...ROLE_OPTIONS, SUPER_ADMIN_OPTION] : ROLE_OPTIONS;

  return (
    <AdminFormShell activeEntity="users" actions={actions}>
      <FormColumn>
        <FormHeader
          eyebrow={mode === 'create' ? 'New user' : 'Edit user'}
          title={mode === 'create' ? 'Add a user' : record?.name ?? 'User'}
          subtitle="Invite a team member and assign their access. Members sign in with Google using their email."
          meta={mode === 'edit' && record ? <RecordMeta id={record.id} createdAt={record.createdAt} /> : undefined}
        />

        <FormSection title="Profile">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 18 }}>
            <div
              aria-hidden
              style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                background: 'rgba(0,229,255,0.12)',
                border: '1px solid rgba(0,229,255,0.4)',
                color: 'var(--accent)',
                fontSize: 22,
                fontWeight: 700,
              }}
              className="mono"
            >
              {initials(name || record?.name || '')}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-faint)' }}>
              Avatar is the member's initials — no photo upload.
            </div>
          </div>

          <Field label="Full name">
            <input style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" />
          </Field>
          <Field
            label="Work email"
            hint={mode === 'edit' ? 'Email is fixed after creation.' : 'They sign in with Google using this address.'}
            error={email.length > 0 && !emailValid ? 'Enter a valid email address.' : null}
          >
            <input
              style={{ ...inputStyle, opacity: mode === 'edit' ? 0.7 : 1 }}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@company.com"
              disabled={mode === 'edit'}
              autoComplete="off"
            />
          </Field>

          {isSuperRole ? (
            <div
              style={{
                fontSize: 12.5,
                color: 'var(--text-dim)',
                background: 'var(--surface-card)',
                border: '1px solid var(--border)',
                borderRadius: 9,
                padding: '10px 12px',
              }}
            >
              Super admins are internal and not scoped to an organization or project.
            </div>
          ) : (
            <>
              {isSuper && (
                <Field label="Organization">
                  <select
                    style={inputStyle}
                    value={orgId}
                    onChange={(e) => setOrgId(e.target.value)}
                    disabled={mode === 'edit'}
                  >
                    <option value="">— select organization —</option>
                    {orgs?.items.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.name}
                      </option>
                    ))}
                  </select>
                </Field>
              )}

              <Field label="Assigned projects" hint="Projects in this organization. (Not yet enforced — informational.)">
                {orgProjects.length > 0 ? (
                  <SegmentedControl
                    options={orgProjects.map((p) => ({ value: p.id, label: p.name }))}
                    isActive={(v) => assigned.includes(v)}
                    onClick={toggleAssigned}
                  />
                ) : (
                  <div style={{ fontSize: 12.5, color: 'var(--text-faint)' }}>
                    No projects in this organization yet.
                  </div>
                )}
              </Field>
            </>
          )}
        </FormSection>

        <FormSection title="Role & permissions">
          <SegmentedControl
            options={roleOptions}
            variant="cards"
            isActive={(v) => v === activeRole}
            onClick={(v) => {
              if (roleLocked) return;
              if (v === 'user' || v === 'admin') setRoleSel(v);
              else if (v === 'super_admin' && canCreateSuper) setRoleSel('super_admin');
            }}
            disabled={roleLocked}
          />
          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{ fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text-faint)' }}>
                Permissions
              </span>
              <span className="mono" style={{ fontSize: 10.5, color: 'var(--text-faint)' }}>
                LOCKED · follows role
              </span>
            </div>
            {PERMS.map((p) => {
              const on = isSuperRole || (roleSel !== 'super_admin' && p.roles.includes(roleSel));
              return (
                <div
                  key={p.name}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '9px 0',
                    borderBottom: '1px solid var(--border-subtle)',
                  }}
                >
                  <span style={{ fontSize: 13, color: on ? 'var(--text-secondary)' : 'var(--text-faint)' }}>{p.name}</span>
                  <ReadonlyToggle on={on} />
                </div>
              );
            })}
          </div>
        </FormSection>

        {error && <ErrorNote>{error}</ErrorNote>}
      </FormColumn>
    </AdminFormShell>
  );
}

/** Disabled toggle that reflects (but can't change) a role-derived permission. */
function ReadonlyToggle({ on }: { on: boolean }) {
  return (
    <span
      style={{
        width: 40,
        height: 22,
        borderRadius: 12,
        position: 'relative',
        flex: 'none',
        background: on ? 'rgba(0,229,255,0.25)' : 'rgba(255,255,255,0.07)',
        border: `1px solid ${on ? 'rgba(0,229,255,0.5)' : 'rgba(255,255,255,0.1)'}`,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 2,
          left: on ? 20 : 3,
          width: 16,
          height: 16,
          borderRadius: '50%',
          background: on ? 'var(--accent)' : '#7d8a99',
          transition: 'left .15s',
        }}
      />
    </span>
  );
}
